// Query keys the Schedules table and the schedule builder share, so a write
// on one refreshes what the other reads, and the schedule list lookup the
// pack pages use to name a pack's source schedule.
import { useCallback, useEffect, useMemo, useState } from 'react'
import { useQuery } from '@tanstack/react-query'
import { prepacksApi } from '@/services/api'
import { packRecipeId } from './prepackCardModel'
import { isRunOfRequest } from './usePrepackRunQueue'

export const scheduleQueryKeys = Object.freeze({
  /** Every schedule with the pack type catalog (`listRecipes`). */
  list: (slug) => ['prepacks', 'recipes', slug],
  /** Every single-schedule query of an organization, for invalidation. */
  details: (slug) => ['prepacks', 'recipe', slug],
  /** One schedule with its rule (`getRecipe`), as the builder loads it. */
  detail: (slug, id) => ['prepacks', 'recipe', slug, id],
  configuration: (slug) => ['prepacks', 'configuration', slug],
  surfaces: (slug) => ['prepacks', 'surfaces', slug],
  regulations: (slug) => ['prepacks', 'regulations', slug],
  /**
   * A schedule's (or a new schedule's builder session's) picture previews for one template and fill (issue 684),
   * under the template's title (`placement/alignment`, #957): the server lists only those composed for it, so a
   * title change asks again.
   */
  bannerPreviews: (slug, recipeId, session, templateId, fill, title) => ['prepacks', 'banner-previews', slug, recipeId || null, session || null, templateId, fill, title || null],
})

/**
 * Marks the schedule list and the single-schedule queries stale after a
 * write: the one schedule's (`ids`) or, without ids, every schedule's of the
 * organization. The builder then loads the rule as saved, not a cached copy
 * a later save would write back.
 */
export function invalidateSchedules(queryClient, slug, ids = null) {
  queryClient.invalidateQueries({ queryKey: scheduleQueryKeys.list(slug) })
  if (Array.isArray(ids)) {
    for (const id of ids) queryClient.invalidateQueries({ queryKey: scheduleQueryKeys.detail(slug, id) })
  } else {
    queryClient.invalidateQueries({ queryKey: scheduleQueryKeys.details(slug) })
  }
}

/**
 * The manual run a request id started, read back when its reply was lost: the
 * run whose slot is `manual:<id>`, as the run queue finds it (usePrepackRunQueue).
 * Null when the server has none, so the run never started.
 */
export async function findManualRun(slug, requestId) {
  const response = await prepacksApi.listAutomationRuns(slug, { request_id: requestId })
  const runs = Array.isArray(response?.runs) ? response.runs : []
  return runs.find((run) => isRunOfRequest(run, requestId)) || null
}

const NO_IDS = Object.freeze([])
const NO_RECHECKS = Object.freeze({ slug: null, asked: new Map(), deleted: new Set() })

/**
 * Names the schedule that generated a pack (#863). Reads the organization's
 * schedules with the Schedules table's own query (same key and shape), so a
 * visit there, or another pack page, already filled it. `ids` are the
 * schedule ids on view: one the loaded list lacks is read again, once, before
 * it counts as deleted, since the list may predate a schedule another
 * operator created meanwhile; when that read fails, the next successful read
 * (a focus, another page) answers for the id instead. Returns
 * `sourceOf(pack)`: null for a hand-built pack, else `{ id, name, missing }`;
 * `missing` only once a read newer than the ask lacked the id. While the list
 * loads, or after a failed read, `name` is null and the builder answers for
 * the id.
 */
export function useScheduleSources(slug, { enabled = true, ids = NO_IDS } = {}) {
  const recipesQuery = useQuery({
    queryKey: scheduleQueryKeys.list(slug),
    queryFn: async () => (await prepacksApi.listRecipes(slug)) || { recipes: [], strategies: [] },
    enabled: Boolean(slug && enabled),
    staleTime: 60_000,
  })
  const names = useMemo(
    () => new Map((recipesQuery.data?.recipes || []).map((recipe) => [recipe.id, recipe.name])),
    [recipesQuery.data],
  )
  const loaded = recipesQuery.isSuccess
  // Per id the loaded list lacked: the list's timestamp when it was read
  // again (`asked`), and whether that read lacked it too (`deleted`). A read
  // newer than the ask answers for an id whose own read failed.
  const [rechecks, setRechecks] = useState(NO_RECHECKS)
  const recheck = rechecks.slug === slug ? rechecks : NO_RECHECKS
  const unknownKey = loaded
    ? [...new Set(ids.filter((id) => id && !names.has(id) && !recheck.asked.has(id)))].sort().join('\n')
    : ''
  const { refetch, dataUpdatedAt } = recipesQuery
  useEffect(() => {
    if (!unknownKey) return
    const unknown = unknownKey.split('\n')
    setRechecks((state) => {
      const base = state.slug === slug ? state : NO_RECHECKS
      return { slug, asked: new Map([...base.asked, ...unknown.map((id) => [id, dataUpdatedAt])]), deleted: base.deleted }
    })
    refetch().then((result) => {
      if (result.status !== 'success') return
      const fresh = new Set((result.data?.recipes || []).map((recipe) => recipe.id))
      const gone = unknown.filter((id) => !fresh.has(id))
      if (gone.length === 0) return
      setRechecks((state) => {
        const base = state.slug === slug ? state : NO_RECHECKS
        return { slug, asked: base.asked, deleted: new Set([...base.deleted, ...gone]) }
      })
    })
  }, [dataUpdatedAt, refetch, slug, unknownKey])

  return useCallback((pack) => {
    const id = packRecipeId(pack)
    if (!id) return null
    const answered = recheck.deleted.has(id) || (recheck.asked.has(id) && dataUpdatedAt > recheck.asked.get(id))
    return { id, name: names.get(id) || null, missing: loaded && !names.has(id) && answered }
  }, [dataUpdatedAt, loaded, names, recheck])
}
